'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, MoreVertical, X, Search, ArrowRight } from 'lucide-react';
import dynamic from 'next/dynamic';
import { cn } from '@/app/utils/cn';
import { NavDemoButtons } from './components/ButtonRequestDemo';
import { hardcodedNavItems, mergeHardcodedFallbacks, NavItemIcon } from './navigation';
import { hardcodedDropdownData } from './utils/dropdownData';

const SearchModal = dynamic(
    () =>
        import('@/components/shared/chat/SearchModal').then((mod) => ({
            default: mod.SearchModal
        })),
    { ssr: false }
);

function NavigationMobile({
    dropdowns,
    navItems,
    useHardcoded = true,
    position,
    promoBannerHeight = 0,
    className
}) {
    const [expandedItems, setExpandedItems] = useState({});
    const [showSearch, setShowSearch] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const dropdownData = useMemo(
        () =>
            useHardcoded
                ? hardcodedDropdownData
                : mergeHardcodedFallbacks(dropdowns) || hardcodedDropdownData,
        [useHardcoded, dropdowns]
    );

    const activeNavItems = useMemo(
        () => (useHardcoded ? hardcodedNavItems : navItems || hardcodedNavItems),
        [useHardcoded, navItems]
    );

    const toggleSearch = useCallback(() => {
        setShowSearch((prev) => !prev);
    }, []);

    const closeSearch = useCallback(() => setShowSearch(false), []);

    useEffect(() => {
        if (!isOpen) {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            setExpandedItems({});
            return;
        }

        // Save current scroll position then fix the body in place so iOS Safari
        // cannot scroll the page behind the open menu.
        const scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            const savedScrollY = parseInt(document.body.style.top || '0', 10);
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.documentElement.style.overflow = '';
            window.scrollTo(0, -savedScrollY);
        };
    }, [isOpen]);

    const toggleExpand = useCallback((key) => {
        setExpandedItems((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const renderNavItems = () => {
        return activeNavItems.map((item) => {
            const hasDropdown = item.hasDropdown;
            const isExpanded = expandedItems[item.key];
            const data = dropdownData[item.key];

            return (
                <div
                    key={item.key}
                    className="rounded-2xl border-b border-gray-100 dark:border-gray-900">
                    {hasDropdown ? (
                        <>
                            <button
                                onClick={() => toggleExpand(item.key)}
                                className="flex w-full items-center justify-between px-6 py-4 text-left text-base font-semibold text-gray-900 dark:text-white">
                                <span>{item.label}</span>
                                <ChevronDown
                                    className={cn(
                                        'size-5 text-gray-400 transition-transform duration-200',
                                        isExpanded && 'rotate-180'
                                    )}
                                />
                            </button>
                            {isExpanded && data && (
                                <div className="bg-white px-6 pb-6 pt-2 dark:bg-gray-900">
                                    {data.columns.map((column, colIdx) => (
                                        <div key={colIdx} className={cn(colIdx > 0 && 'mt-2')}>
                                            {column.heading && (
                                                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                                                    {column.heading}
                                                </p>
                                            )}
                                            <div>
                                                {column.items.map((subItem) => (
                                                    <a
                                                        key={subItem.label}
                                                        href={subItem.href}
                                                        className="group flex items-start gap-3 py-3">
                                                        {subItem.icon && (
                                                            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 transition-colors group-hover:bg-fuschia-100 dark:bg-gray-800 dark:group-hover:bg-fuschia-900/40">
                                                                <span className="text-gray-500 transition-colors group-hover:text-fuschia [&>svg]:size-4">
                                                                    <NavItemIcon
                                                                        icon={subItem.icon}
                                                                    />
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium text-gray-900 transition-colors group-hover:text-fuschia dark:text-white">
                                                                {subItem.label}
                                                            </span>
                                                            {subItem.description && (
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {subItem.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {data.promo && (
                                        <div
                                            className={cn(
                                                'pt-2 mt-3 border-gray-100 dark:border-gray-800 rounded-xl p-4',
                                                data.promo.bgClass ||
                                                    'bg-fuschia-50 dark:bg-fuschia-900/20'
                                            )}>
                                            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fuschia">
                                                {data.promo.eyebrow}
                                            </p>
                                            <p className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                                                {data.promo.title}
                                            </p>
                                            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                                                {data.promo.description}
                                            </p>
                                            <a
                                                href={data.promo.cta?.href}
                                                className="flex items-center gap-1.5 text-xs font-bold text-fuschia">
                                                {data.promo.cta?.linkName}
                                                <div className="w-4 min-w-4">
                                                    <ArrowRight className="size-3" />
                                                </div>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <a
                            href={item.href}
                            className="block px-6 py-4 text-base font-semibold text-gray-900 dark:text-white">
                            {item.label}
                        </a>
                    )}
                </div>
            );
        });
    };

    return (
        <div className={className}>
            <div className="flex items-center gap-4">
                <button
                    aria-label="toggleSearch"
                    onClick={toggleSearch}
                    className="flex items-center justify-center p-2">
                    <Search className="size-5 hover:text-fuschia dark:hover:text-fuschia-500" />
                </button>
                <button
                    aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
                    onClick={() => setIsOpen(!isOpen)}>
                    {isOpen ? <X className="size-6" /> : <MoreVertical className="size-6" />}
                </button>
            </div>
            <nav
                style={{
                    top: `${position}px`,
                    height: `calc(${promoBannerHeight ? '97vh' : '90vh'} - ${position + promoBannerHeight}px)`,
                    left: isOpen ? 10 : 0,
                    right: isOpen ? 10 : 0
                }}
                className={cn(
                    'overflow-hidden fixed z-50 border border-gray-300 dark:border-gray-800 bg-white dark:bg-gray-950 rounded-2xl flex flex-col transition-transform duration-300 ease-in-out',
                    isOpen ? 'translate-y-0' : 'translate-y-[5%]',
                    isOpen ? 'opacity-100' : 'opacity-0',
                    !isOpen && 'pointer-events-none'
                )}>
                <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
                    {renderNavItems()}
                    <div className="mt-auto shrink-0 border-t border-gray-100 p-6 dark:border-gray-800">
                        <NavDemoButtons
                            className="w-full flex-col"
                            buttonClassName="justify-center w-full"
                        />
                    </div>
                </div>
            </nav>
            {showSearch && <SearchModal isOpen onClose={closeSearch} />}
        </div>
    );
}

export default NavigationMobile;
